import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { X, Sparkles, Loader2, RotateCcw, Save, Wand2, Plus } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import storage from '../utils/storage';
import { getBackendUrl } from '../utils/api';

/**
 * AI Content Editor Modal — Phase 1 (2026-02-24)
 * ------------------------------------------------
 * Admin-only modal that lets an operator either:
 *  - edit an existing section body ("edit" mode) via GPT-5.6 Luna, or
 *  - generate a brand new topic ("add" mode) inside a section.
 *
 * Flow:
 *  1) Operator types instruction/topic hint → clicks "Generate preview"
 *  2) Backend returns proposed text (dry_run=true) — modal shows diff
 *  3) Operator either "Save" (dry_run=false persist) or "Cancel"
 */
const EDIT_PRESETS = [
  { key: 'shorten', label: 'Πιο σύντομο', instruction: 'Κάνε το κείμενο πιο σύντομο, κράτησε μόνο τα ουσιώδη.' },
  { key: 'expand', label: 'Πιο εκτενές', instruction: 'Επέκτεινε το κείμενο με περισσότερες λεπτομέρειες και παραδείγματα.' },
  { key: 'grammar', label: 'Διόρθωση γραμματικής', instruction: 'Διόρθωσε ορθογραφικά και γραμματικά λάθη χωρίς να αλλάξεις το νόημα.' },
  { key: 'energetic', label: 'Πιο ζωντανό', instruction: 'Ξαναγράψε το πιο ζωντανά και πιασάρικα για ραδιοφωνική εκπομπή.' },
  { key: 'formal', label: 'Πιο επίσημο', instruction: 'Κάνε τον τόνο πιο επίσημο και επαγγελματικό.' },
  { key: 'casual', label: 'Πιο casual', instruction: 'Κάνε τον τόνο πιο casual και φιλικό.' },
];

const AIContentEditorModal = ({ open, onClose, mode, sectionKey, sectionTitle, currentText, date, onSaved }) => {
  const [instruction, setInstruction] = useState('');
  const [lengthHint, setLengthHint] = useState('medium');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [preview, setPreview] = useState(null);
  const [rollbackId, setRollbackId] = useState(null);

  const API = getBackendUrl();

  useEffect(() => {
    if (open) {
      setInstruction('');
      setLengthHint('medium');
      setPreview(null);
      setRollbackId(null);
    }
  }, [open]);

  if (!open) return null;

  const handleGeneratePreview = async () => {
    if (!instruction.trim()) {
      toast.error(mode === 'add' ? 'Δώσε θέμα' : 'Δώσε οδηγία επεξεργασίας');
      return;
    }
    setLoading(true);
    setPreview(null);
    try {
      const token = storage.getItem('token');
      const url = mode === 'add' ? `${API}/api/admin/content/ai-add-topic` : `${API}/api/admin/content/ai-edit`;
      const body = mode === 'add'
        ? { section_key: sectionKey, topic_hint: instruction, length_hint: lengthHint, date, dry_run: true }
        : { section_key: sectionKey, instruction, current_text: currentText, date, dry_run: true };
      const response = await axios.post(url, body, {
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        timeout: 60000,
      });
      setPreview(response.data);
    } catch (e) {
      const detail = e.response?.data?.detail || e.message || 'Σφάλμα';
      toast.error(`Σφάλμα LLM: ${detail}`);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!preview) return;
    setSaving(true);
    try {
      const token = storage.getItem('token');
      const url = mode === 'add' ? `${API}/api/admin/content/ai-add-topic` : `${API}/api/admin/content/ai-edit`;
      const body = mode === 'add'
        ? { section_key: sectionKey, topic_hint: instruction, length_hint: lengthHint, date, dry_run: false, append: true }
        : { section_key: sectionKey, instruction, current_text: currentText, date, dry_run: false };
      const response = await axios.post(url, body, {
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        timeout: 60000,
      });
      setRollbackId(response.data.rollback_id);
      toast.success('✅ Αποθηκεύτηκε! Rollback ID: ' + response.data.rollback_id?.slice(0, 8));
      if (onSaved) onSaved(response.data);
      setTimeout(() => onClose(), 800);
    } catch (e) {
      const detail = e.response?.data?.detail || e.message || 'Σφάλμα';
      toast.error(`Σφάλμα αποθήκευσης: ${detail}`);
    } finally {
      setSaving(false);
    }
  };

  const proposedText = preview?.edited || preview?.generated || '';

  return (
    <div
      className="fixed inset-0 z-[100] bg-black/70 backdrop-blur-sm flex items-center justify-center p-4"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
      data-testid="ai-editor-modal"
    >
      <div className="bg-gradient-to-br from-[#2D0C4B] to-[#1A0730] rounded-2xl border border-purple-500/30 shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-5 border-b border-white/10">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center">
              {mode === 'add' ? <Plus className="w-5 h-5 text-white" /> : <Wand2 className="w-5 h-5 text-white" />}
            </div>
            <div>
              <h3 className="text-lg font-bold text-white" data-testid="ai-editor-title">
                {mode === 'add' ? 'Δημιουργία νέου θέματος' : 'Επεξεργασία με AI'}
              </h3>
              <p className="text-xs text-purple-200">
                Section: <Badge className="ml-1 bg-purple-500/20 text-purple-100">{sectionTitle || sectionKey}</Badge>
                <span className="ml-2 text-white/50">GPT-5.6 Luna · Ελληνικά</span>
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-white/10 rounded-lg transition-colors text-white/70 hover:text-white"
            data-testid="ai-editor-close-btn"
            aria-label="Close"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-5 space-y-4">
          {/* Instruction / Topic input */}
          <div>
            <Label className="text-white mb-2 block">
              {mode === 'add' ? 'Θέμα του νέου κειμένου' : 'Οδηγία επεξεργασίας'}
            </Label>
            {mode === 'edit' && (
              <div className="flex flex-wrap gap-2 mb-2">
                {EDIT_PRESETS.map((p) => (
                  <button
                    key={p.key}
                    type="button"
                    onClick={() => setInstruction(p.instruction)}
                    className="text-xs px-3 py-1 rounded-full bg-purple-500/20 hover:bg-purple-500/40 text-purple-100 border border-purple-500/30 transition-colors"
                    data-testid={`ai-preset-${p.key}`}
                  >
                    {p.label}
                  </button>
                ))}
              </div>
            )}
            <Textarea
              value={instruction}
              onChange={(e) => setInstruction(e.target.value)}
              placeholder={mode === 'add'
                ? 'π.χ. Γράψε ένα quiz με 3 ερωτήσεις για την ιστορία της Eurovision'
                : 'π.χ. Ξαναγράψε το πιο πιασάρικα, κράτησε τα ίδια σημεία'}
              rows={3}
              className="bg-white/5 border-white/20 text-white placeholder:text-white/40"
              data-testid="ai-editor-instruction"
            />
          </div>

          {/* Length hint (add mode only) */}
          {mode === 'add' && (
            <div>
              <Label className="text-white mb-2 block">Έκταση</Label>
              <div className="flex gap-2">
                {['short', 'medium', 'long'].map((h) => (
                  <button
                    key={h}
                    type="button"
                    onClick={() => setLengthHint(h)}
                    className={`px-4 py-2 rounded-lg text-sm transition-colors ${
                      lengthHint === h
                        ? 'bg-purple-500 text-white'
                        : 'bg-white/5 text-white/70 hover:bg-white/10'
                    }`}
                    data-testid={`ai-length-${h}`}
                  >
                    {h === 'short' ? 'Σύντομο' : h === 'medium' ? 'Μεσαίο' : 'Εκτενές'}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Generate button */}
          {!preview && (
            <Button
              onClick={handleGeneratePreview}
              disabled={loading || !instruction.trim()}
              className="w-full bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white"
              data-testid="ai-editor-generate-btn"
            >
              {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Sparkles className="w-4 h-4 mr-2" />}
              {loading ? 'Παράγεται...' : 'Δες Preview'}
            </Button>
          )}

          {/* Preview / Diff */}
          {preview && (
            <div className="space-y-3">
              {mode === 'edit' && (
                <div>
                  <Label className="text-white/70 mb-1 block text-xs">Πρωτότυπο</Label>
                  <div
                    className="text-sm text-white/60 bg-black/30 rounded-lg p-3 max-h-40 overflow-y-auto border border-white/10 whitespace-pre-wrap"
                    data-testid="ai-editor-original"
                  >
                    {preview.original}
                  </div>
                </div>
              )}
              <div>
                <Label className="text-green-300 mb-1 block text-xs flex items-center gap-1">
                  <Sparkles className="w-3 h-3" /> {mode === 'add' ? 'Νέο κείμενο' : 'Προτεινόμενο κείμενο'}
                </Label>
                <div
                  className="text-sm text-white bg-green-500/10 rounded-lg p-3 max-h-72 overflow-y-auto border border-green-500/30 whitespace-pre-wrap"
                  data-testid="ai-editor-proposed"
                >
                  {proposedText}
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Footer actions */}
        {preview && (
          <div className="flex items-center justify-between gap-3 p-5 border-t border-white/10 bg-black/30">
            <Button
              variant="outline"
              onClick={() => setPreview(null)}
              disabled={saving}
              className="border-white/20 text-white hover:bg-white/10"
              data-testid="ai-editor-retry-btn"
            >
              <RotateCcw className="w-4 h-4 mr-2" /> Ξανά
            </Button>
            <div className="flex gap-2">
              <Button
                variant="outline"
                onClick={onClose}
                disabled={saving}
                className="border-white/20 text-white hover:bg-white/10"
                data-testid="ai-editor-cancel-btn"
              >
                Άκυρο
              </Button>
              <Button
                onClick={handleSave}
                disabled={saving}
                className="bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700 text-white"
                data-testid="ai-editor-save-btn"
              >
                {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
                {saving ? 'Αποθήκευση...' : (mode === 'add' ? 'Προσθήκη & Αποθήκευση' : 'Αποθήκευση')}
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AIContentEditorModal;
